import { signIn } from 'next-auth/react'
import { FormattedMessage, useIntl } from 'react-intl'
import Link from 'next/link'
import { FormikValues } from 'formik'
import { useRouter } from 'next/router'
import * as Yup from 'yup'
import React, { ReactNode, useEffect, useState } from 'react'
import { Text } from '@chakra-ui/react'
import { useApolloClient } from '@apollo/client'

import {
  Button,
  ErrorMessage,
  ErrorMessageBox,
  FieldRow,
  FieldSet,
} from '@gaia-family/ds'
import { UserErrorCode } from '@gaia-family/members-graphql/client'
import { PublicErrorMsg, assertUnreachable } from '@gaia-family/lib'

import { isPermittedDestination } from '../../../lib/auth'
import { Fields, FormManager } from '..'
import { emailSchema } from '../validation/commonSchemas'

import { Routes } from '@hooks/useRouting'
import { useLocalisedConfig } from '@hooks/useLocalisedConfig'
import { Country } from '@graphql'

type LoginFormProps = {
  isSubmitting: boolean
  serverError: ErrorMessage | null
}

const SignInForm = ({ isSubmitting, serverError }: LoginFormProps) => {
  const [jsLoaded, setJsLoaded] = useState(false)
  useEffect(() => {
    setJsLoaded(true)
  }, [])

  return (
    <FieldSet>
      <Fields.Email
        name="email"
        data-cy="email"
        label="Email"
        placeholder="Enter your email address"
      />
      <Fields.Password
        name="password"
        data-cy="password"
        label="Password"
        placeholder="Enter your password"
        afterInput={
          <Link href={Routes.passwordReset} passHref>
            <Text as="a" textStyle="cta" size="xs">
              <FormattedMessage defaultMessage="Forgot password?" />
            </Text>
          </Link>
        }
      />
      {serverError ? <ErrorMessageBox {...serverError} /> : null}
      <FieldRow>
        <Button
          flex={1}
          type="submit"
          isDisabled={!jsLoaded || isSubmitting}
          data-cy="login"
        >
          <ButtonLabel jsLoaded={jsLoaded} isSubmitting={isSubmitting} />
        </Button>
        <LocalisedCreateAccountCta />
      </FieldRow>
    </FieldSet>
  )
}

const ButtonLabel = ({
  jsLoaded,
  isSubmitting,
}: {
  jsLoaded: boolean
  isSubmitting: boolean
}) => {
  if (!jsLoaded) return <FormattedMessage defaultMessage="Page loading..." />
  if (isSubmitting) return <FormattedMessage defaultMessage="Logging in..." />
  return <FormattedMessage defaultMessage="Sign in" />
}

const LocalisedCreateAccountCta = () => {
  const { country } = useLocalisedConfig()

  switch (country) {
    case Country.Gb:
      return (
        <Link href={Routes.welcome} passHref>
          <Button as="a" variant="tertiary">
            <FormattedMessage defaultMessage="Create account" />
          </Button>
        </Link>
      )
    case Country.Us:
      return null
    default:
      return assertUnreachable(country)
  }
}

export const SignInFormManager = () => {
  const router = useRouter()
  const { formatMessage } = useIntl()
  const [serverError, setServerError] = useState<null | ErrorMessage>(null)
  const { resetStore } = useApolloClient()

  const SignInErrors: Record<string, ReactNode> = {
    [UserErrorCode.INVALID_LOGIN]: formatMessage({
      defaultMessage: 'Please check your email and password and try again.',
    }),
  }

  const onError = (errorCode: string) => {
    const description = SignInErrors[errorCode] || PublicErrorMsg.SERVICE_ERROR

    return setServerError({
      summary: formatMessage({ defaultMessage: 'An error occurred' }),
      description,
    })
  }

  const signUserIn = async (values: FormikValues) => {
    const signInResponse = await signIn('signIn', {
      ...values,
      redirect: false,
    })

    if (signInResponse?.error) {
      onError(signInResponse.error)

      return
    }

    resetStore()

    const destination = (router.query['redirect'] as string) || Routes.profile

    await router.push(
      isPermittedDestination(destination) ? destination : Routes.profile
    )
  }

  const validationSchema = Yup.object().shape({
    email: emailSchema(),
    password: Yup.string().required(
      formatMessage({ defaultMessage: 'Password cannot be empty' })
    ),
  })

  const queryParams = []

  for (const param in router.query) {
    if (router.query[param] !== 'undefined') {
      queryParams.push(`${param}=${router.query[param]}`)
    }
  }

  return (
    <FormManager
      initialValues={{
        email: '',
        password: '',
      }}
      onSubmit={signUserIn}
      validationSchema={validationSchema}
      alignSelf="stretch"
    >
      {({ isSubmitting }) => (
        <SignInForm serverError={serverError} isSubmitting={isSubmitting} />
      )}
    </FormManager>
  )
}
